import { avatarColors, avatarNeutral } from "@canucci/base/people";
import {
  BLACK_HEX,
  WHITE_HEX,
  RED_700_HEX,
  RED_900_HEX,
  accentRamp,
  blueRamp,
  coralRamp,
  goldRamp,
  INK_HEX,
  INK_OVERLAY_ALPHA_80,
  lavenderRamp,
  MINT_SOFT_HEX,
  mintRamp,
  grayRamp,
  oceanRamp,
  pinkRamp,
} from "../colors/primitives";
import { hexColorWithOpacity } from "../colors/utils";

/**
 * L1 — named brand colors for product code (app-site, collect-ui, canvas-ui, slides).
 * Avatar colors come from base; other roles resolve from local L0 ramps.
 * Values do not depend on Mantine CSS variables.
 */
export const brandColorTokens = {
  avatarColors,
  avatarNeutral,
  pageBackground: oceanRamp[50],
  loadingPlaceholder: oceanRamp[100],
  loadingPlaceholderHighlight: oceanRamp[200],
  loadingPlaceholderOnLavender: lavenderRamp[300],
  loadingPlaceholderHighlightOnLavender: lavenderRamp[400],
  bodyText: INK_HEX,
  textSecondary: grayRamp[700],
  textMuted: grayRamp[700],
  surfaceBorder: oceanRamp[200],
  chartSeries: oceanRamp[600],
  chartTrack: oceanRamp[100],
  chartPositive: oceanRamp[700],
  chartNegative: coralRamp[800],
  chartSignal: lavenderRamp[700],
  chartCategoryColors: [oceanRamp[700], coralRamp[800], lavenderRamp[700], grayRamp[700]] as const,
  textMutedOnDark: hexColorWithOpacity(oceanRamp[50], 0.78),
  endBackground: BLACK_HEX,

  slideCountPaper: WHITE_HEX,
  slideCountCard: oceanRamp[50],
  slideCountSelectedCard: oceanRamp[100],
  slideCountMutedText: oceanRamp[600],
  slideCountAction: oceanRamp[700],
  slideCountActionHover: oceanRamp[800],
  slideCanvas: oceanRamp[700],
  slideSurface: oceanRamp[900],
  slideSurfaceMid: oceanRamp[800],
  slideSurfaceInset: oceanRamp[700],
  slideSurfaceLight: oceanRamp[200],
  slideSurfaceLightBorder: oceanRamp[400],
  slideTextOnDark: oceanRamp[50],
  slideEmphasisText: goldRamp[500],
  slideNotificationBackground: oceanRamp[100],
  slideNotificationBorder: oceanRamp[200],
  slideDockedBarEdge: oceanRamp[800],
  slideControlFill: oceanRamp[800],
  slideControlEdge: oceanRamp[800],
  slideControlMenuHover: oceanRamp[700],
  slideDockedBarBackdrop: `color-mix(in srgb, ${oceanRamp[700]} 88%, ${oceanRamp[50]})`,

  slideDivider: goldRamp[500],
  slideSecondaryLight: oceanRamp[200],
  slideSecondaryLightBorder: oceanRamp[400],
  slideCtaFill: goldRamp[500],
  slideNavChipActiveBorder: goldRamp[500],
  /** Selected theme chip label on light (paper) chip background. */
  slideNavChipActiveText: oceanRamp[700],
  slideSkeletonShimmerHighlight: oceanRamp[400],

  ctaGold: goldRamp[500],
  paperAccentBorder: goldRamp[500],
  highlightSurface: goldRamp[50],
  highlightSurfaceBorder: goldRamp[200],
  strongHighlightSurface: goldRamp[200],
  strongHighlightSurfaceBorder: goldRamp[400],
  textOnHighlight: INK_HEX,

  collectPageBackground: oceanRamp[50],
  collectQuestionAccent: oceanRamp[600],
  collectIntroBubbleBackground: oceanRamp[100],
  collectIntroBubbleAccentBar: oceanRamp[600],
  collectIntroBubbleLabel: oceanRamp[600],
  collectIntroBubbleBorder: oceanRamp[300],
  collectTextareaFocusRing: `0 0 0 3px ${hexColorWithOpacity(oceanRamp[600], 0.16)}`,

  authShellBackground: oceanRamp[100],
  authShellHover: oceanRamp[200],
  infoSoftBackground: oceanRamp[100],
  fgOnDarkDim: hexColorWithOpacity(oceanRamp[50], 0.66),

  scrimOnLight: INK_OVERLAY_ALPHA_80,

  badgeRequiredBackground: lavenderRamp[200],
  badgeDueBackground: goldRamp[100],
  badgeReportBackground: lavenderRamp[200],
  /** Facilitator home workshop “Open” pill (gold 500, #FFD472). */
  facilitatorWorkshopOpenBadge: goldRamp[500],

  facilitatorSectionTile: oceanRamp[50],
  facilitatorSectionTileBorder: oceanRamp[50],
  /** Facilitator home interactive section tiles (workshops, people, FAQ) on hover / focus. */
  facilitatorSectionTileHover: oceanRamp[100],
  /** Sidebar + page shell (ocean 50, #F4F9FA). */
  facilitatorSidebarBackground: oceanRamp[50],
  /** Inner main content paper in facilitator AppShell (white); home section cards sit on this. */
  facilitatorMainContentPaper: "#FFFFFF",
  /** Sidebar nav selected row (ocean 100, #E3F0F2). */
  facilitatorSidebarNavActive: oceanRamp[100],
  facilitatorSidebarNavActiveText: INK_HEX,
  /** Inactive sidebar nav row hover — midpoint between shell (50) and active (100). */
  facilitatorSidebarNavHover: `color-mix(in srgb, ${oceanRamp[50]} 50%, ${oceanRamp[100]} 50%)`,
  /** Combobox/list option hover — same as inactive sidebar nav hover. */
  facilitatorListOptionHover: `color-mix(in srgb, ${oceanRamp[50]} 50%, ${oceanRamp[100]} 50%)`,
  /** Combobox/list selected option — same as sidebar nav active row (ocean 100). */
  facilitatorListOptionSelected: oceanRamp[100],
  facilitatorListOptionSelectedText: INK_HEX,
  facilitatorSectionTileActive: oceanRamp[700],
  facilitatorSectionTileActiveHover: oceanRamp[800],
  facilitatorSectionTileActiveText: oceanRamp[50],
  facilitatorPageHeading: oceanRamp[700],
  facilitatorAgendaLink: oceanRamp[900],
  facilitatorAgendaBadgeBackground: hexColorWithOpacity(grayRamp[600], 0.1),
  facilitatorAgendaBadgeText: grayRamp[600],
  facilitatorSessionWarningSurface: oceanRamp[500],
  facilitatorSessionWarningBorder: goldRamp[700],
  facilitatorSessionWarningIcon: goldRamp[400],
  facilitatorSupportLink: oceanRamp[800],
  facilitatorPageSubtitle: grayRamp[900],
  facilitatorEyebrow: accentRamp[600],
  facilitatorIconTileBackground: accentRamp[50],
  facilitatorIconTileText: accentRamp[700],
  facilitatorDecoratedLightSurface: accentRamp[50],
  facilitatorDecoratedLightBorder: accentRamp[200],
  facilitatorTextOnDark: WHITE_HEX,
  /** Workshop Review step title on accordion control hover. */
  facilitatorPageHeadingHover: oceanRamp[800],
  /** Facilitator home / dashboard section headings (Workshops, People, …). */
  facilitatorSectionTitle: oceanRamp[700],
  /** Home greeting “Welcome back” (ocean 700 at ~70% opacity, #003C48B2). */
  facilitatorHomeWelcomeGreeting: hexColorWithOpacity(oceanRamp[700], 0xb2 / 0xff),
  facilitatorWelcomeMuted: oceanRamp[600],
  facilitatorFaqSurface: goldRamp[50],
  facilitatorFaqSurfaceBorder: goldRamp[200],
  facilitatorSupportSurface: lavenderRamp[200],
  facilitatorSupportSurfaceBorder: lavenderRamp[200],
  facilitatorTableSurface: oceanRamp[50],
  facilitatorTableRowHover: oceanRamp[100],
  /** People page colleague cards on mobile/tablet (+100 vs table row hover on ocean 50 surface). */
  facilitatorPeopleColleagueCardBackground: oceanRamp[100],
  facilitatorPeopleColleagueCardHover: oceanRamp[200],
  facilitatorPeopleColleagueCardSelected: oceanRamp[200],
  facilitatorPrimaryCta: coralRamp[400],
  facilitatorPrimaryCtaHover: coralRamp[500],
  facilitatorPrimaryCtaText: INK_HEX,
  /** Facilitator home resource card surface (ocean 50). */
  facilitatorHomeResourceCard: oceanRamp[50],
  facilitatorHomeResourceCardBorder: oceanRamp[50],
  facilitatorHomeResourceCardTitle: oceanRamp[700],
  facilitatorHomeResourceCardCaption: oceanRamp[600],
  /** Resource content category icon tiles (lavender 200, blue 200, coral 400). */
  resourceCategoryWorkshopBackground: lavenderRamp[200],
  resourceCategoryLinkBackground: blueRamp[200],
  resourceCategoryCanvasBackground: coralRamp[400],
  /** Facilitator home resource card Open pill (ocean 200, #D2E7EA). */
  facilitatorHomeResourceOpenButton: oceanRamp[200],
  facilitatorHomeResourceOpenButtonHover: oceanRamp[300],
  facilitatorHomeResourceOpenButtonText: INK_HEX,
  /** Home people card Contact button at rest (ocean 100). */
  facilitatorHomeContactButton: oceanRamp[100],
  /** Contact button when the people card is hovered (ocean 200). */
  facilitatorHomeContactButtonOnCardHover: oceanRamp[200],
  /** Contact button when the button itself is hovered (ocean 300). */
  facilitatorHomeContactButtonHover: oceanRamp[300],
  /** Facilitator FAQ accordion row when open (ocean 100, #E3F0F2). */
  facilitatorFaqItemOpen: oceanRamp[100],
  /** Edit profile field labels (Name, E-mail, Profile photo, …). */
  profileFieldLabel: INK_HEX,
  profileRemoveButtonText: RED_700_HEX,
  profileRemoveButtonHoverText: RED_900_HEX,
  /** Edit profile upload photo button fill (ocean 100, #E3F0F2). */
  profileUploadButton: oceanRamp[100],
  profileUploadButtonHover: oceanRamp[300],
  /** Edit profile focused field background (ocean 50, #F4F9FA). */
  profileFieldFocus: oceanRamp[50],
  /** Edit profile focused / open field border (ocean 200, #D2E7EA). */
  profileFieldFocusBorder: oceanRamp[200],
  /** Edit profile editable field hover border (ocean 700). */
  profileFieldHoverBorder: oceanRamp[700],
  /** Edit profile disabled / read-only field background (gray 100, #F1F3F5). */
  profileFieldDisabledBackground: grayRamp[100],
  /** Edit profile disabled / read-only field border — unchanged on hover/focus (gray 300). */
  profileFieldDisabledBorder: grayRamp[300],
  /** Edit profile save button fill (ocean 700, #003C48). */
  profileSaveButton: oceanRamp[700],
  profileSaveButtonHover: oceanRamp[800],
  profileSaveButtonText: oceanRamp[50],
  facilitatorSoftButtonSurface: oceanRamp[50],
  facilitatorSoftButtonBorder: oceanRamp[300],
  facilitatorStepOpenSurface: oceanRamp[100],
  /** Separator under an open workshop step header (ocean 200). */
  facilitatorStepAccordionDivider: oceanRamp[200],
  /** Workshop step accordion row when collapsed (ocean 50, matches section tiles). */
  facilitatorStepClosedSurface: oceanRamp[50],
  /** Closed workshop accordion row hover (ocean 100); open rows do not hover. */
  facilitatorStepClosedSurfaceHover: oceanRamp[100],
  /** Unselected workshop Yes/No outline button hover (ocean 50). */
  facilitatorStepAnswerOutlineHover: oceanRamp[50],
  facilitatorStepLeadRule: oceanRamp[700],
  badgeCoral: coralRamp[400],
  badgePink: pinkRamp[200],
  badgeLavender: lavenderRamp[200],
  badgeMint: mintRamp[400],
  /** Facilitator home survey card Done pill (#D9F0C6). */
  facilitatorHomeSurveyDoneBadge: MINT_SOFT_HEX,

  themeIdentityMid: [oceanRamp[500], oceanRamp[600], oceanRamp[700]] as const,
} as const;

export type BrandColorTokenKey = keyof typeof brandColorTokens;
